import * as THREE from 'three';
import { TreeGroup } from '../../../../../@types/types';
import { Viewer } from '../Viewer-Factory/Viewer';
import { useViewerStore } from '../../../../../store/store';

export const GHOST_TREE_COLOR = 0xff0000;
export const TREE_SELECT_COLOR = 0x0000ff;
export const SIMPLE_TREE_NORMAL_COLOR = 0x7acb6a;
export const TREE_SELECT_COLOR_INTENSITY = 1;

function getTreeCrown(treeGroup: TreeGroup): any {
    return treeGroup.children?.find((child: any) => child.name === 'crown') ?? treeGroup.children?.[0];
}

function isSimpleTreeCrown(crown: any): boolean {
    if (crown?.userData?.isSimpleTreeLook !== undefined) {
        return !!crown.userData.isSimpleTreeLook;
    }
    return useViewerStore.getState().isSimpleTreeLook;
}

function getLeafMap(child: any, baseMaterial: THREE.Material) {
    if (child.mapSrc) return child.mapSrc;
    const mat = baseMaterial as THREE.MeshStandardMaterial;
    if (mat.map) {
        child.mapSrc = mat.map;
        return mat.map;
    }
    return null;
}

function restoreNormalCrownMaterial(crown: any) {
    const mat = crown.material as THREE.MeshStandardMaterial;
    const isSimpleLook = isSimpleTreeCrown(crown);
    mat.color.setHex(crown.oriColor ?? (isSimpleLook ? SIMPLE_TREE_NORMAL_COLOR : 0x90ee90));
    mat.map = isSimpleLook ? null : (crown.mapSrc ?? null);
    mat.transparent = isSimpleLook ? false : !!crown.mapSrc;
    mat.opacity = 1;
    mat.emissive.setHex(0x000000);
    mat.emissiveIntensity = 0;
    mat.side = isSimpleLook ? THREE.FrontSide : THREE.BackSide;
    mat.needsUpdate = true;
}

/** Selection highlight for live and ghosted trees (crown + trunk). */
export function applyTreeSelectionAppearance(treeGroup: TreeGroup, selected: boolean) {
    treeGroup.children.forEach((child: any) => {
        if (child.name === 'wrapper' || !child.material) return;

        const mat = child.material as THREE.MeshStandardMaterial;
        if (!('emissive' in mat)) return;

        if (selected) {
            mat.emissive.setHex(TREE_SELECT_COLOR);
            mat.emissiveIntensity = treeGroup.delStatus
                ? Math.max(TREE_SELECT_COLOR_INTENSITY, 1.2)
                : TREE_SELECT_COLOR_INTENSITY;
        } else if (treeGroup.delStatus) {
            const baseMaterial = (child.userData._ghostSavedMaterial ?? mat) as THREE.MeshStandardMaterial;
            const leafMap = getLeafMap(child, baseMaterial);
            mat.emissive.setHex(GHOST_TREE_COLOR);
            mat.emissiveIntensity = leafMap ? 0.15 : 0.3;
        } else {
            mat.emissive.setHex(0x000000);
            mat.emissiveIntensity = 0;
        }

        mat.needsUpdate = true;
    });
}

/** Red ghost styling for all ghosted trees (independent of GhostOnlyGreenImpactTrees rules). */
export function applyTreeGhostAppearance(treeGroup: TreeGroup, ghosted: boolean) {
    treeGroup.delStatus = ghosted;
    treeGroup.children.forEach((child: any) => {
        if (child.name !== 'trunk') return;

        if (ghosted) {
            if (child.userData._ghostTrunkWasVisible === undefined) {
                child.userData._ghostTrunkWasVisible = child.visible;
            }
            child.visible = false;
        } else if (child.userData._ghostTrunkWasVisible !== undefined) {
            child.visible = child.userData._ghostTrunkWasVisible;
            delete child.userData._ghostTrunkWasVisible;
        } else {
            child.visible = true;
        }
    });

    const crown = getTreeCrown(treeGroup);
    if (!crown?.material) return;

    if (ghosted) {
        if (!crown.userData._ghostSavedMaterial) {
            crown.userData._ghostSavedMaterial = crown.material;
        }
        const baseMaterial = crown.userData._ghostSavedMaterial as THREE.MeshStandardMaterial;
        const leafMap = getLeafMap(crown, baseMaterial);
        const isSimpleLook = isSimpleTreeCrown(crown);
        const ghostMat = baseMaterial.clone() as THREE.MeshStandardMaterial;

        ghostMat.color.setHex(GHOST_TREE_COLOR);
        ghostMat.map = isSimpleLook ? null : leafMap;
        ghostMat.transparent = isSimpleLook ? false : true;
        ghostMat.opacity = isSimpleLook ? 1 : (leafMap ? 0.88 : 0.92);
        ghostMat.emissive.setHex(isSimpleLook ? 0x220000 : 0x550000);
        ghostMat.emissiveIntensity = isSimpleLook ? 0.35 : (leafMap ? 0.15 : 0.3);
        ghostMat.depthWrite = true;
        ghostMat.side = THREE.DoubleSide;
        ghostMat.needsUpdate = true;
        crown.material = ghostMat;
    } else if (crown.userData._ghostSavedMaterial) {
        crown.material = crown.userData._ghostSavedMaterial;
        delete crown.userData._ghostSavedMaterial;
    } else {
        restoreNormalCrownMaterial(crown);
    }

    crown.castShadow = !ghosted;

    if (!ghosted && treeGroup.select) {
        applyTreeSelectionAppearance(treeGroup, true);
    }
}

export function reapplyAllGhostTreeAppearances(self: Viewer) {
    self.treeGroup.children.forEach((tree: TreeGroup) => {
        if (tree.delStatus) applyTreeGhostAppearance(tree, true);
    });
    syncAllTreeSelectionHighlights(self);
}

export function syncAllTreeSelectionHighlights(self: Viewer) {
    (self.treeGroup.children as any[]).forEach((tree: TreeGroup) => {
        applyTreeSelectionAppearance(tree, !!tree.select);
    });
}

export function reapplyAllTreeSelectionAppearances(self: Viewer) {
    self.treeGroup.children.forEach((tree: TreeGroup) => {
        applyTreeSelectionAppearance(tree, !!tree.select);
    });
}

export function reapplyAllTreeVisualStates(self: Viewer) {
    reapplyAllGhostTreeAppearances(self);
    reapplyAllTreeSelectionAppearances(self);
}
